/**
 * DULCERNA — Tema Shopify
 * Carrito (Ajax Cart API), filtros del catálogo, favoritos y menú móvil.
 */
(() => {
  'use strict';

  const cfg = window.Dulcerna || { routes: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  // ---------------------------------------------------------------------------
  // Utilidades
  // ---------------------------------------------------------------------------
  const esc = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const withDelimiters = (cents, precision, thousands, decimal) => {
    const num = (Number(cents) / 100).toFixed(precision);
    const [whole, frac] = num.split('.');
    return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (frac ? decimal + frac : '');
  };

  const formatMoney = (cents) => {
    const format = cfg.moneyFormat || '${{amount_no_decimals_with_comma_separator}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    const map = {
      amount: () => withDelimiters(cents, 2, ',', '.'),
      amount_no_decimals: () => withDelimiters(cents, 0, ',', '.'),
      amount_with_comma_separator: () => withDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => withDelimiters(cents, 0, '.', ','),
      amount_with_apostrophe_separator: () => withDelimiters(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => withDelimiters(cents, 0, ' ', '.'),
      amount_with_space_separator: () => withDelimiters(cents, 2, ' ', ',')
    };
    const fn = map[match[1]] || map.amount_no_decimals_with_comma_separator;
    return format.replace(match[0], fn());
  };

  const safeStorage = {
    get(key, fallback) {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* almacenamiento no disponible */
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Aviso flotante
  // ---------------------------------------------------------------------------
  const toastEl = $('#site-toast');
  let toastTimer;
  const showToast = (msg) => {
    if (!toastEl) return;
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2600);
  };

  // ---------------------------------------------------------------------------
  // Encabezado: transparente sobre el hero, sólido al bajar
  // ---------------------------------------------------------------------------
  const header = $('[data-header]');
  const hero = $('#hero-section');

  const updateHeader = () => {
    if (!header || header.dataset.solid === 'true') return;
    const past = hero ? hero.getBoundingClientRect().bottom <= 80 : window.scrollY > 22;
    header.classList.toggle('scrolled', past);
  };
  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });
  window.addEventListener('resize', updateHeader);

  const menuToggle = $('[data-menu-toggle]');
  if (menuToggle && header) {
    menuToggle.addEventListener('click', () => {
      const open = header.classList.toggle('nav-open');
      menuToggle.setAttribute('aria-expanded', String(open));
    });
  }

  // ---------------------------------------------------------------------------
  // Carrito (Ajax Cart API)
  // ---------------------------------------------------------------------------
  let cart = null;
  let deliveryMethod = 'domicilio';
  let selectedSede = '';

  const drawer = $('#cart-drawer');

  const openDrawer = () => {
    if (!drawer) return;
    drawer.classList.add('is-open');
    document.body.classList.add('drawer-open');
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
  };

  const cartItemMarkup = (item, index) => {
    const hasVariant = item.variant_title && item.variant_title !== 'Default Title';
    const image = item.featured_image && item.featured_image.url
      ? `<img src="${esc(item.featured_image.url)}" alt="${esc(item.product_title)}" class="cart-item-img" />`
      : '';
    const line = index + 1;
    return `
      <div class="cart-item-row" data-line="${line}">
        ${image}
        <div class="cart-item-details">
          <div class="cart-item-title">${esc(item.product_title)}</div>
          ${hasVariant ? `<div class="cart-item-opt">${esc(item.variant_title)}</div>` : ''}
          <div class="cart-item-price">${formatMoney(item.final_price)}</div>
        </div>
        <div class="cart-item-stepper">
          <button type="button" class="cart-qty-btn" data-cart-qty="${line}" data-delta="-1" aria-label="Reducir">−</button>
          <span class="cart-qty-num">${item.quantity}</span>
          <button type="button" class="cart-qty-btn" data-cart-qty="${line}" data-delta="1" aria-label="Aumentar">+</button>
        </div>
        <button type="button" class="cart-item-remove-btn" data-cart-remove="${line}" aria-label="Eliminar ${esc(item.product_title)}">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      </div>`;
  };

  const renderCart = () => {
    if (!cart) return;
    const count = cart.item_count;
    const total = formatMoney(cart.total_price);

    $$('[data-cart-badge]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('has-items', count > 0);
    });
    $$('[data-cart-count]').forEach((el) => (el.textContent = count));
    $$('[data-cart-subtotal], [data-cart-total]').forEach((el) => (el.textContent = total));

    const bar = $('#mobile-cart-bar');
    if (bar) bar.classList.toggle('is-visible', count > 0);

    const itemsHtml = cart.items.length
      ? cart.items.map(cartItemMarkup).join('')
      : `<div class="cart-empty-message">
           <div class="empty-icon">🛒</div>
           <p>Tu pedido está vacío.</p>
           <span class="empty-sub">Selecciona productos deliciosos del catálogo.</span>
         </div>`;
    $$('[data-cart-items]').forEach((el) => (el.innerHTML = itemsHtml));

    $$('[data-checkout-btn]').forEach((btn) => (btn.disabled = count === 0));
  };

  const request = async (url, payload) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'No se pudo completar la acción');
    return data;
  };

  const refreshCart = async () => {
    try {
      const res = await fetch(cfg.routes.cartJs, { headers: { Accept: 'application/json' } });
      cart = await res.json();
      renderCart();
    } catch (e) {
      /* sin conexión: se mantiene el estado renderizado por el servidor */
    }
  };

  const addToCart = async (variantId, quantity = 1, label = '') => {
    try {
      await request(cfg.routes.cartAdd, { items: [{ id: Number(variantId), quantity }] });
      await refreshCart();
      showToast(label ? `✓ Agregado: ${label}` : '✓ Agregado al pedido');
      return true;
    } catch (err) {
      showToast(err.message);
      return false;
    }
  };

  const changeLine = async (line, quantity) => {
    try {
      cart = await request(cfg.routes.cartChange, { line, quantity });
      renderCart();
    } catch (err) {
      showToast(err.message);
    }
  };

  // Entrega: domicilio o recoger en tienda
  const setDelivery = (method) => {
    deliveryMethod = method;
    $$('[data-delivery]').forEach((r) => (r.checked = r.value === method));
    $$('[data-sede-wrap]').forEach((w) => (w.style.display = method === 'tienda' ? 'block' : 'none'));
  };

  const setSede = (sede) => {
    selectedSede = sede;
    $$('[data-sede-select]').forEach((s) => (s.value = sede));
  };

  const initDelivery = () => {
    const firstSede = $('[data-sede-select]');
    if (firstSede) selectedSede = firstSede.value;
  };

  const goToCheckout = async () => {
    const attributes = {
      'Método de entrega': deliveryMethod === 'tienda' ? 'Recoger en tienda' : 'Entrega a domicilio'
    };
    if (deliveryMethod === 'tienda' && selectedSede) attributes['Sede de recogida'] = selectedSede;
    try {
      await request(cfg.routes.cartUpdate, { attributes });
    } catch (e) {
      /* si falla guardar el atributo, se continúa al pago igualmente */
    }
    window.location.href = cfg.routes.checkout;
  };

  // ---------------------------------------------------------------------------
  // Catálogo del inicio: filtros por etiqueta y búsqueda instantánea
  // ---------------------------------------------------------------------------
  const catalog = $('[data-catalog]');
  const state = { category: 'todos', query: '', showAll: false };

  const applyCatalog = () => {
    if (!catalog) return;
    const initial = Number(catalog.dataset.initial) || 8;
    const cards = $$('[data-product-card]', catalog);
    const q = state.query.trim().toLowerCase();
    const limited = state.category === 'todos' && !q && !state.showAll;
    let visible = 0;

    cards.forEach((card) => {
      const tags = card.dataset.tags || '';
      const matchCat = state.category === 'todos' || tags.includes(` ${state.category} `);
      const matchQuery = !q || (card.dataset.title || '').includes(q);
      const index = Number(card.dataset.index);
      const show = limited ? index <= initial : matchCat && matchQuery;
      card.hidden = !show;
      if (show) visible += 1;
    });

    const empty = $('[data-catalog-empty]', catalog);
    if (empty) empty.hidden = visible > 0;

    $$('[data-filter-chip]', catalog).forEach((chip) => {
      chip.classList.toggle('is-active', chip.dataset.filterChip === state.category);
    });
  };

  const scrollToCatalog = () => {
    if (catalog) catalog.scrollIntoView({ behavior: 'smooth' });
  };

  // ---------------------------------------------------------------------------
  // Favoritos
  // ---------------------------------------------------------------------------
  let favorites = safeStorage.get('dulcerna_favs', []);

  const paintFavorites = () => {
    $$('[data-fav-handle]').forEach((btn) => {
      btn.classList.toggle('is-active', favorites.includes(btn.dataset.favHandle));
    });
  };

  // ---------------------------------------------------------------------------
  // Eventos delegados
  // ---------------------------------------------------------------------------
  document.addEventListener('click', async (e) => {
    const target = e.target.closest('button, a');
    if (!target) return;

    // Abrir carrito
    if (target.matches('[data-open-cart]')) {
      e.preventDefault();
      await refreshCart();
      openDrawer();
      return;
    }

    if (target.id === 'cart-drawer-close') {
      closeDrawer();
      return;
    }

    // Líneas del carrito
    if (target.matches('[data-cart-qty]')) {
      const line = Number(target.dataset.cartQty);
      const item = cart && cart.items[line - 1];
      if (item) changeLine(line, item.quantity + Number(target.dataset.delta));
      return;
    }

    if (target.matches('[data-cart-remove]')) {
      changeLine(Number(target.dataset.cartRemove), 0);
      return;
    }

    if (target.matches('[data-checkout-btn]')) {
      goToCheckout();
      return;
    }

    // Stepper de las tarjetas
    if (target.matches('[data-step-minus], [data-step-plus]')) {
      const valEl = $('[data-step-val]', target.closest('.card-qty-stepper'));
      if (valEl) {
        const current = parseInt(valEl.textContent, 10) || 1;
        const next = target.matches('[data-step-plus]') ? current + 1 : Math.max(1, current - 1);
        valEl.textContent = next;
      }
      return;
    }

    // Agregar desde una tarjeta
    if (target.matches('[data-add-btn]')) {
      const card = target.closest('[data-product-card]');
      const select = card && $('[data-variant-select]', card);
      const variantId = select ? select.value : target.dataset.variantId;
      const valEl = card && $('[data-step-val]', card);
      const qty = valEl ? parseInt(valEl.textContent, 10) || 1 : 1;
      const title = card ? $('.card-title', card).textContent.trim() : '';

      const ok = await addToCart(variantId, qty, title);
      if (ok) {
        const original = target.innerHTML;
        target.classList.add('is-added');
        target.innerHTML =
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg><span class="add-text">Agregado</span>';
        setTimeout(() => {
          target.classList.remove('is-added');
          target.innerHTML = original;
        }, 1200);
      }
      return;
    }

    // Favoritos
    if (target.matches('[data-fav-handle]')) {
      const handle = target.dataset.favHandle;
      const idx = favorites.indexOf(handle);
      if (idx >= 0) {
        favorites.splice(idx, 1);
        showToast('Eliminado de favoritos');
      } else {
        favorites.push(handle);
        showToast('Guardado en favoritos ❤️');
      }
      safeStorage.set('dulcerna_favs', favorites);
      paintFavorites();
      return;
    }

    // Filtros del catálogo del inicio
    if (target.matches('[data-filter-chip]')) {
      e.preventDefault();
      state.category = target.dataset.filterChip;
      state.showAll = true;
      applyCatalog();
      return;
    }

    if (target.matches('[data-view-all]')) {
      e.preventDefault();
      state.category = 'todos';
      state.showAll = true;
      applyCatalog();
      scrollToCatalog();
      return;
    }

    if (target.matches('[data-reset-filters]')) {
      state.category = 'todos';
      state.query = '';
      state.showAll = true;
      const search = $('#catalog-search');
      if (search) search.value = '';
      applyCatalog();
      return;
    }

    // Página de producto: cantidad
    if (target.matches('[data-qty-minus], [data-qty-plus]')) {
      const input = $('[data-qty-input]', target.closest('form'));
      if (input) {
        const current = parseInt(input.value, 10) || 1;
        input.value = target.matches('[data-qty-plus]') ? current + 1 : Math.max(1, current - 1);
      }
      return;
    }

    // Cerrar menú móvil al elegir un enlace
    if (target.matches('.nav-cat-item') && header) {
      header.classList.remove('nav-open');
    }
  });

  document.addEventListener('change', (e) => {
    const el = e.target;

    // Variantes en tarjeta: actualizar precio
    if (el.matches('[data-variant-select]')) {
      const card = el.closest('[data-product-card]');
      const priceEl = card && $('[data-card-price]', card);
      const option = el.options[el.selectedIndex];
      if (priceEl && option) priceEl.textContent = option.dataset.price;
      return;
    }

    // Variantes en la página de producto
    if (el.matches('[data-product-variant-select]')) {
      const priceEl = $('[data-product-price]');
      const option = el.options[el.selectedIndex];
      if (priceEl && option) priceEl.textContent = option.dataset.price;
      return;
    }

    if (el.matches('[data-delivery]')) {
      setDelivery(el.value);
      return;
    }

    if (el.matches('[data-sede-select]')) {
      setSede(el.value);
    }
  });

  // Búsqueda instantánea en el inicio; en otras páginas envía el formulario
  const searchInput = $('#catalog-search');
  const searchForm = searchInput && searchInput.closest('form');
  if (catalog && searchInput) {
    searchInput.addEventListener('input', () => {
      state.query = searchInput.value;
      state.showAll = true;
      applyCatalog();
    });
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        scrollToCatalog();
      });
    }
  }

  // Agregar desde la página de producto sin salir de ella
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.js-product-form');
    if (!form) return;
    e.preventDefault();
    const data = new FormData(form);
    const ok = await addToCart(data.get('id'), parseInt(data.get('quantity'), 10) || 1, $('.page-heading')?.textContent.trim());
    if (ok) openDrawer();
  });

  // Teclado y clic fuera del drawer
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawer();
      if (header) header.classList.remove('nav-open');
    }
  });

  if (drawer) {
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) closeDrawer();
    });
  }

  // ---------------------------------------------------------------------------
  // Inicio
  // ---------------------------------------------------------------------------
  initDelivery();
  setDelivery('domicilio');
  paintFavorites();
  applyCatalog();
  refreshCart();
})();
